import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  RestoreFromTrash as RestoreIcon,
  FilterListOff as FilterListOffIcon,
} from '@mui/icons-material';
import { ModePaiement, ModePaiementCreate } from '../types';
import { modesPaiementAPI } from '../services/api';
import AlertDialog from './AlertDialog';
import useAlert from '../hooks/useAlert';
import { getModuleTableTheme } from '../theme/tableTheme';
import CustomTablePagination from './CustomTablePagination';
import PermissionButton from './PermissionButton';
import ColumnFilter from './ColumnFilter';
import { useColumnFilters } from '../hooks/useColumnFilters';
import { extractErrorMessage } from '../utils/errorHandler';

const CONFIG_COLOR = '#1976D2';

const MODE_FILTER_SCHEMA = {
  nom:         { type: 'text', param: 'nom' },
  description: { type: 'text', param: 'description' },
  created_at:  { type: 'dateRange', paramFrom: 'created_from', paramTo: 'created_to' },
} as const;

const ModePaiementManagement: React.FC = () => {
  const [modes, setModes] = useState<ModePaiement[]>([]);
  const [deletedModes, setDeletedModes] = useState<ModePaiement[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedMode, setSelectedMode] = useState<ModePaiement | null>(null);
  const [formData, setFormData] = useState<ModePaiementCreate>({ nom: '', description: '' });
  const [formLoading, setFormLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tabValue, setTabValue] = useState(0);
  const { alertState, showError, showSuccess, showConfirm, hideAlert } = useAlert();
  const { filters, update, reset, hasActive } = useColumnFilters(MODE_FILTER_SCHEMA, {
    onChange: () => setPage(0),
  });

  const fetchModes = async () => {
    try {
      setLoading(true);
      const data = await modesPaiementAPI.getAll();
      setModes(data);
    } catch (err) {
      showError('Erreur lors du chargement des modes de paiement');
      console.error('Error fetching modes:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDeletedModes = async () => {
    try {
      setLoading(true);
      const data = await modesPaiementAPI.getDeleted();
      setDeletedModes(data);
    } catch (err) {
      showError('Erreur lors du chargement des modes supprimés');
      console.error('Error fetching deleted modes:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(0);
    if (newValue === 1) {
      fetchDeletedModes();
    }
  };

  const handleRestore = async (mode: ModePaiement) => {
    showConfirm(
      `Êtes-vous sûr de vouloir restaurer le mode "${mode.nom}" ?`,
      async () => {
        try {
          await modesPaiementAPI.restore(mode.id);
          showSuccess('Mode de paiement restauré avec succès');
          fetchDeletedModes();
          fetchModes();
        } catch (err: any) {
          showError(extractErrorMessage(err));
        }
      },
      { title: 'Confirmer la restauration', confirmText: 'Restaurer', cancelText: 'Annuler' }
    );
  };

  const handleSubmit = async () => {
    if (!formData.nom.trim()) return;

    try {
      setFormLoading(true);

      if (selectedMode) {
        await modesPaiementAPI.update(selectedMode.id, formData);
        showSuccess('Mode de paiement modifié avec succès');
      } else {
        await modesPaiementAPI.create(formData);
        showSuccess('Mode de paiement créé avec succès');
      }

      setIsFormOpen(false);
      setSelectedMode(null);
      setFormData({ nom: '', description: '' });
      fetchModes();
    } catch (err: any) {
      showError(extractErrorMessage(err));
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (mode: ModePaiement) => {
    try {
      await modesPaiementAPI.delete(mode.id);
      setModes(modes.filter(m => m.id !== mode.id));
      showSuccess('Mode de paiement supprimé avec succès');
    } catch (err: any) {
      showError(extractErrorMessage(err));
    }
  };

  const openForm = (mode?: ModePaiement) => {
    setSelectedMode(mode || null);
    setFormData({
      nom: mode?.nom || '',
      description: mode?.description || ''
    });
    setIsFormOpen(true);
  };

  useEffect(() => {
    fetchModes();
  }, []);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredModes = useMemo(
    () =>
      modes.filter((m) => {
        if (filters.nom && !m.nom.toLowerCase().includes(filters.nom.toLowerCase())) return false;
        if (filters.description && !(m.description || '').toLowerCase().includes(filters.description.toLowerCase())) return false;
        if (filters.created_at.from && new Date(m.created_at) < new Date(filters.created_at.from)) return false;
        if (filters.created_at.to && new Date(m.created_at) > new Date(`${filters.created_at.to}T23:59:59`)) return false;
        return true;
      }),
    [modes, filters]
  );

  const paginatedModes = filteredModes.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box p={3} sx={{ width: '100%', maxWidth: 'none' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">
          Gestion des Modes de Paiement
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {tabValue === 0 && hasActive() && (
            <Button
              size="small"
              startIcon={<FilterListOffIcon />}
              onClick={reset}
              sx={{ color: 'text.secondary' }}
            >
              Effacer les filtres
            </Button>
          )}
          {tabValue === 0 && (
            <PermissionButton
              moduleName="Config"
              operation="add"
              miniModuleName="Modes Paiement"
              variant="contained"
              onClick={() => openForm()}
              startIcon={<AddIcon />}
              sx={{
                backgroundColor: '#1976D2',
                '&:hover': {
                  backgroundColor: '#1565C0',
                }
              }}
            >
              Nouveau Mode
            </PermissionButton>
          )}
        </Box>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Modes Actifs" />
          <Tab label="Corbeille" icon={<DeleteIcon />} iconPosition="start" />
        </Tabs>
      </Box>

      {tabValue === 0 && (
        <>
          <TableContainer component={Paper} sx={getModuleTableTheme('configuration')}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Nom
                      <ColumnFilter type="text" value={filters.nom} onChange={(v) => update('nom', v)} label="nom" color={CONFIG_COLOR} />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Description
                      <ColumnFilter type="text" value={filters.description} onChange={(v) => update('description', v)} label="description" color={CONFIG_COLOR} />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Date de Création
                      <ColumnFilter type="dateRange" value={filters.created_at} onChange={(v) => update('created_at', v)} label="date de création" color={CONFIG_COLOR} />
                    </Box>
                  </TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      Chargement...
                    </TableCell>
                  </TableRow>
                ) : filteredModes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      {hasActive() ? 'Aucun mode ne correspond aux filtres' : 'Aucun mode de paiement trouvé'}
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedModes.map((mode) => (
                    <TableRow key={mode.id}>
                      <TableCell>{mode.nom}</TableCell>
                      <TableCell>{mode.description || '-'}</TableCell>
                      <TableCell>
                        {new Date(mode.created_at).toLocaleDateString('fr-FR')}
                      </TableCell>
                      <TableCell align="center">
                        <PermissionButton
                          moduleName="Config"
                          operation="edit"
                          miniModuleName="Modes Paiement"
                          component="IconButton"
                          onClick={() => openForm(mode)}
                        >
                          <EditIcon />
                        </PermissionButton>
                        <PermissionButton
                          moduleName="Config"
                          operation="delete"
                          miniModuleName="Modes Paiement"
                          component="IconButton"
                          onClick={() => {
                            showConfirm(
                              `Êtes-vous sûr de vouloir supprimer le mode "${mode.nom}" ?`,
                              () => handleDelete(mode),
                              {
                                title: 'Confirmer la suppression',
                                confirmText: 'Supprimer',
                                cancelText: 'Annuler'
                              }
                            );
                          }}
                          color="error"
                        >
                          <DeleteIcon />
                        </PermissionButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <CustomTablePagination
            count={filteredModes.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}

      {tabValue === 1 && (
        <>
          <TableContainer component={Paper} sx={getModuleTableTheme('configuration')}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nom</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Date de Suppression</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      Chargement...
                    </TableCell>
                  </TableRow>
                ) : deletedModes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      Aucun mode supprimé
                    </TableCell>
                  </TableRow>
                ) : (
                  deletedModes
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((mode) => (
                      <TableRow key={mode.id}>
                        <TableCell>{mode.nom}</TableCell>
                        <TableCell>{mode.description || '-'}</TableCell>
                        <TableCell>
                          {mode.deleted_at
                            ? new Date(mode.deleted_at).toLocaleDateString('fr-FR')
                            : '-'}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Restaurer">
                            <IconButton
                              onClick={() => handleRestore(mode)}
                              color="primary"
                            >
                              <RestoreIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <CustomTablePagination
            count={deletedModes.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}

      {/* Form Dialog */}
      <Dialog open={isFormOpen} onClose={() => setIsFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedMode ? 'Modifier le mode de paiement' : 'Nouveau mode de paiement'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nom"
            fullWidth
            variant="filled"
            value={formData.nom}
            onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
            sx={{ mt: 2 }}
          />
          <TextField
            margin="dense"
            label="Description (optionnel)"
            fullWidth
            variant="filled"
            multiline
            rows={3}
            value={formData.description || ''}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsFormOpen(false)} disabled={formLoading}>
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={formLoading || !formData.nom.trim()}
            sx={{
              backgroundColor: '#4CAF50',
              '&:hover': {
                backgroundColor: '#388E3C',
              }
            }}
          >
            {formLoading ? 'Sauvegarde...' : 'Sauvegarder'}
          </Button>
        </DialogActions>
      </Dialog>

      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        showCancel={alertState.showCancel}
      />
    </Box>
  );
};

export default ModePaiementManagement;
